/**
 * Funnel runtime provider — session id, assign, evaluate-step, accept/decline, template fetch.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { createFunnelClient, FunnelApiError, type FunnelClientConfig } from './create-funnel-client';
import type {
  DisplaySettings,
  FunnelEvaluateStepResponse,
  PageContext,
  StepNavigationResponse,
  TemplateContentResponse,
  FunnelEvaluateStepRequest,
} from './types';
import { FunnelAnalyticsProvider, mergeAnalytics, type FunnelAnalytics } from './analytics';
import { ProductImageProvider, type ResolveProductImage } from './product-image-context';
import { funnelLogger } from './logger';
import { buildCookieString } from './cookie-utils';
import { extractBannerPlacement } from './banner-placement';
import {
  FUNNEL_SESSION_ID_KEY as SESSION_ID_KEY,
  FUNNEL_SESSION_COUNT_KEY as SESSION_COUNT_KEY,
  ASSIGNED_FUNNEL_ID_KEY,
  ASSIGNED_FUNNEL_NAME_KEY,
  ASSIGNED_FUNNEL_EXPIRES_AT_KEY,
  DECLINED_STEPS_KEY,
  CURRENT_STEP_KEY_PREFIX,
  syncFunnelRuntimeVersion,
} from './runtime-storage';
const MAX_ASSIGN_RETRIES = 2;
const ASSIGN_TIMEOUT_MS = 15_000;

function getNavigationType(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const entries = window.performance?.getEntriesByType?.('navigation');
    const firstEntry = Array.isArray(entries) ? entries[0] : undefined;
    if (firstEntry && 'type' in firstEntry && typeof firstEntry.type === 'string') {
      return firstEntry.type;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function shouldForceFreshSessionOnLoad(): boolean {
  return Boolean(import.meta.env?.DEV) && getNavigationType() === 'reload';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string | null): void {
  if (typeof document === 'undefined') return;
  document.cookie = buildCookieString(name, value, window.location.hostname);
}

export interface AcceptOfferParams {
  stepId: string;
  productId: string;
  variantId?: string;
  quantity?: number;
  action?: 'add_to_cart' | 'add_to_order';
  /** Required for post-purchase (add_to_order). The order ID from checkout completion. */
  orderId?: string;
}

export interface FunnelContextValue {
  config: FunnelClientConfig;
  sessionId: string;
  assignedFunnelId: string | null;
  assignedFunnelName: string | null;
  isAssignedFunnelLocked: boolean;
  loading: boolean;
  assignError: string | null;
  assignPageFunnel: (pageContext: PageContext, signal?: AbortSignal) => Promise<{ funnelId: string; funnelName: string; cached: boolean }>;
  lockAssignedFunnel: (funnelId: string, funnelName?: string | null) => void;
  clearAssignedFunnel: () => void;
  evaluatePageStep: (request: Omit<FunnelEvaluateStepRequest, 'sessionId'> & { funnelId?: string }) => Promise<FunnelEvaluateStepResponse>;
  acceptOffer: (params: AcceptOfferParams) => Promise<StepNavigationResponse>;
  declineOffer: (stepId: string, orderId?: string) => Promise<StepNavigationResponse>;
  fetchTemplate: (templateId: string) => Promise<TemplateContentResponse>;
  activeExperience: {
    funnelId: string;
    stepId: string;
    experienceType: string | null;
    placement?: string;
    displaySettings?: DisplaySettings | null;
    bannerPlacement?: ReturnType<typeof extractBannerPlacement>;
  } | null;
  setActiveExperience: (experience: {
    funnelId: string;
    stepId: string;
    experienceType: string | null;
    placement?: string;
    displaySettings?: DisplaySettings | null;
    bannerPlacement?: ReturnType<typeof extractBannerPlacement>;
  } | null) => void;
}

const FunnelContext = createContext<FunnelContextValue | undefined>(undefined);

function buildBaseContext(): Partial<PageContext> {
  if (typeof window === 'undefined') return {};
  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const now = new Date();

  const urlParameters: Record<string, string> = {};
  try {
    new URLSearchParams(window.location.search).forEach((value, key) => {
      urlParameters[key] = value;
    });
  } catch {
    /* ignore */
  }

  let sessionCount = 0;
  try {
    sessionCount = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10) || 0;
  } catch {
    /* ignore */
  }

  return {
    deviceType: isMobile ? 'mobile' : 'desktop',
    browserLanguage: navigator.language,
    dayOfWeek: now.getDay(),
    timeOfDay: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
    currentDate: now.toISOString().slice(0, 10),
    landingUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    urlParameters: Object.keys(urlParameters).length > 0 ? urlParameters : undefined,
    sessionCount,
  };
}

function getCurrentURLPath(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const { pathname, search } = window.location;
  if (!pathname) return undefined;
  return `${pathname}${search ?? ''}`;
}

function withResolvedURL<T extends Partial<PageContext>>(pageContext: T): T & { url?: string } {
  return {
    ...pageContext,
    url: pageContext.url ?? getCurrentURLPath(),
  };
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    return (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
  }
  try {
    if (shouldForceFreshSessionOnLoad()) {
      const staleCurrentStepKeys: string[] = [];
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(CURRENT_STEP_KEY_PREFIX)) {
          staleCurrentStepKeys.push(key);
        }
      }

      const currentSessionCount = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10) || 0;
      sessionStorage.setItem(SESSION_COUNT_KEY, String(currentSessionCount + 1));

      const id = (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
      sessionStorage.setItem(SESSION_ID_KEY, id);
      sessionStorage.removeItem(ASSIGNED_FUNNEL_ID_KEY);
      sessionStorage.removeItem(ASSIGNED_FUNNEL_NAME_KEY);
      sessionStorage.removeItem(ASSIGNED_FUNNEL_EXPIRES_AT_KEY);
      sessionStorage.removeItem(DECLINED_STEPS_KEY);
      for (const key of staleCurrentStepKeys) {
        sessionStorage.removeItem(key);
      }
      writeCookie(SESSION_ID_KEY, id);
      writeCookie(ASSIGNED_FUNNEL_ID_KEY, null);
      writeCookie(ASSIGNED_FUNNEL_NAME_KEY, null);
      writeCookie(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, null);
      writeCookie(DECLINED_STEPS_KEY, null);
      if (typeof document !== 'undefined') {
        const rawCookies = document.cookie ? document.cookie.split('; ') : [];
        for (const cookie of rawCookies) {
          const name = cookie.split('=')[0];
          if (name.startsWith(CURRENT_STEP_KEY_PREFIX)) {
            writeCookie(name, null);
          }
        }
      }
      return id;
    }

    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;

    const cookieSessionId = readCookie(SESSION_ID_KEY);
    if (cookieSessionId) {
      sessionStorage.setItem(SESSION_ID_KEY, cookieSessionId);
      return cookieSessionId;
    }

    const currentSessionCount = parseInt(sessionStorage.getItem(SESSION_COUNT_KEY) ?? '0', 10) || 0;
    sessionStorage.setItem(SESSION_COUNT_KEY, String(currentSessionCount + 1));

    const id = (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
    sessionStorage.setItem(SESSION_ID_KEY, id);
    writeCookie(SESSION_ID_KEY, id);
    return id;
  } catch {
    return (typeof globalThis.crypto?.randomUUID === "function" ? globalThis.crypto.randomUUID.call(globalThis.crypto) : Math.random().toString(36).slice(2) + Date.now().toString(36));
  }
}

function readStoredValue(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const sessionValue = sessionStorage.getItem(key);
    if (sessionValue) return sessionValue;
    const cookieValue = readCookie(key);
    if (cookieValue) {
      sessionStorage.setItem(key, cookieValue);
      return cookieValue;
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredValue(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null || value === '') {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, value);
    }
    writeCookie(key, value);
  } catch {
    /* ignore */
  }
}

function readAssignedExpiry(): string | null {
  return readStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY);
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) return false;
  return parsed <= Date.now();
}

export interface FunnelProviderProps {
  children: ReactNode;
  /** Funnels API base URL and tenant (same as storefront-layout env). */
  config: FunnelClientConfig;
  /** Optional deploy/runtime fingerprint used to invalidate stale browser funnel state after updates. */
  runtimeVersion?: string | null;
  countryCode?: string;
  /** Optional dataLayer / analytics hooks (defaults to no-ops). */
  analytics?: Partial<FunnelAnalytics>;
  /** Optional: resolve product hero image when offer.imageUrl is empty. */
  resolveProductImage?: ResolveProductImage;
}

export function FunnelProvider({
  children,
  config,
  runtimeVersion,
  countryCode,
  analytics: analyticsPartial,
  resolveProductImage,
}: FunnelProviderProps) {
  const stableConfig = useMemo<FunnelClientConfig>(
    () => ({
      apiBaseUrl: config.apiBaseUrl,
      tenantId: config.tenantId,
      checkoutMiddlewareUrl: config.checkoutMiddlewareUrl,
    }),
    [config.apiBaseUrl, config.tenantId, config.checkoutMiddlewareUrl],
  );

  const client = useMemo(
    () => createFunnelClient(stableConfig),
    [stableConfig],
  );

  useState(() => {
    syncFunnelRuntimeVersion(runtimeVersion);
    return true;
  });

  const [sessionId] = useState(getOrCreateSessionId);
  const [assignedFunnelId, setAssignedFunnelId] = useState<string | null>(() => {
    if (shouldForceFreshSessionOnLoad()) {
      return null;
    }
    const expiresAt = readAssignedExpiry();
    if (isExpired(expiresAt)) {
      writeStoredValue(ASSIGNED_FUNNEL_ID_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_NAME_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, null);
      return null;
    }
    return readStoredValue(ASSIGNED_FUNNEL_ID_KEY);
  });
  const [assignedFunnelName, setAssignedFunnelName] = useState<string | null>(() => {
    if (shouldForceFreshSessionOnLoad()) {
      return null;
    }
    const expiresAt = readAssignedExpiry();
    if (isExpired(expiresAt)) {
      return null;
    }
    return readStoredValue(ASSIGNED_FUNNEL_NAME_KEY);
  });
  const [assignedFunnelExpiresAt, setAssignedFunnelExpiresAt] = useState<string | null>(() => {
    if (shouldForceFreshSessionOnLoad()) {
      return null;
    }
    const expiresAt = readAssignedExpiry();
    return isExpired(expiresAt) ? null : expiresAt;
  });
  const [isAssignedFunnelLocked, setIsAssignedFunnelLocked] = useState(() => (
    shouldForceFreshSessionOnLoad()
      ? false
      : Boolean(readStoredValue(ASSIGNED_FUNNEL_ID_KEY)) && !isExpired(readAssignedExpiry())
  ));
  const [loading, setLoading] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [activeExperience, setActiveExperience] = useState<FunnelContextValue['activeExperience']>(null);

  const setAssignedState = useCallback((funnelId: string | null, funnelName: string | null, locked: boolean, expiresAt?: string | null) => {
    setAssignedFunnelId(funnelId);
    setAssignedFunnelName(funnelName);
    setAssignedFunnelExpiresAt(expiresAt ?? null);
    setIsAssignedFunnelLocked(locked);
    if (funnelId) {
      writeStoredValue(ASSIGNED_FUNNEL_ID_KEY, funnelId);
      writeStoredValue(ASSIGNED_FUNNEL_NAME_KEY, funnelName);
      writeStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, expiresAt ?? null);
    } else {
      writeStoredValue(ASSIGNED_FUNNEL_ID_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_NAME_KEY, null);
      writeStoredValue(ASSIGNED_FUNNEL_EXPIRES_AT_KEY, null);
    }
  }, []);

  const lockAssignedFunnel = useCallback((funnelId: string, funnelName?: string | null) => {
    setAssignedState(funnelId, funnelName ?? assignedFunnelName ?? null, true, assignedFunnelExpiresAt);
  }, [assignedFunnelExpiresAt, assignedFunnelName, setAssignedState]);

  const clearAssignedFunnel = useCallback(() => {
    setAssignedState(null, null, false);
  }, [setAssignedState]);

  const ensureAssignedFunnelNotExpired = useCallback((): boolean => {
    if (!assignedFunnelId || !isExpired(assignedFunnelExpiresAt)) {
      return false;
    }
    setAssignedState(null, null, false, null);
    return true;
  }, [assignedFunnelExpiresAt, assignedFunnelId, setAssignedState]);

  const assignPageFunnel = useCallback(
    async (pageContext: PageContext, signal?: AbortSignal): Promise<{ funnelId: string; funnelName: string; cached: boolean }> => {
      const baseCtx = buildBaseContext();
      const mergedContext: PageContext = withResolvedURL({ ...baseCtx, ...pageContext });

      const locale = (typeof window !== 'undefined' ? navigator.language : null) || 'en-US';
      const navCountry = locale.split('-')[1]?.toUpperCase();
      const customerCountry = countryCode ?? mergedContext.country ?? navCountry;
      const countryCurrencyMap: Record<string, string> = {
        US: 'USD',
        CA: 'CAD',
        GB: 'GBP',
        AU: 'AUD',
        NL: 'EUR',
        SE: 'SEK',
        DE: 'EUR',
        DK: 'DKK',
        FI: 'EUR',
        IE: 'EUR',
        FR: 'EUR',
      };
      const customerCurrency = customerCountry ? countryCurrencyMap[customerCountry] : undefined;
      const deviceType = mergedContext.deviceType ?? (/Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop');
      const referrerSource = typeof document !== 'undefined' ? document.referrer || undefined : undefined;

      setAssignError(null);
      setLoading(true);

      for (let attempt = 0; attempt <= MAX_ASSIGN_RETRIES; attempt++) {
        try {
          const controller = signal ? null : new AbortController();
          const activeSignal = signal ?? controller?.signal;
          const timeoutId = controller ? setTimeout(() => controller.abort(), ASSIGN_TIMEOUT_MS) : null;

          const result = await client.assignFunnel(
            {
              sessionId,
              customerCurrency,
              customerCountry,
              urlPath: mergedContext.url,
              pageType: mergedContext.pageType,
              pageContext: mergedContext,
              deviceType,
              referrerSource,
            },
            activeSignal,
          );

          if (timeoutId) clearTimeout(timeoutId);

          if (result.funnelId) {
            setAssignedState(result.funnelId, result.funnelName ?? null, result.cached, result.expiresAt ?? null);
            funnelLogger.info('Funnel assigned for page', {
              funnelId: result.funnelId,
              funnelName: result.funnelName ?? undefined,
              cached: result.cached,
              pageType: mergedContext.pageType,
            });
          } else {
            funnelLogger.warn('No funnel was assigned for this page', {
              pageType: mergedContext.pageType,
              urlPath: mergedContext.url ?? null,
              customerCountry: customerCountry ?? null,
              customerCurrency: customerCurrency ?? null,
              reason: 'no_matching_funnel_or_entry_step',
            });
            clearAssignedFunnel();
          }

          setLoading(false);
          return {
            funnelId: result.funnelId ?? '',
            funnelName: result.funnelName ?? '',
            cached: result.cached,
          };
        } catch (err) {
          if (err instanceof FunnelApiError && err.status === 404) {
            funnelLogger.info('Funnel assign returned no active funnels', {
              status: 404,
              pageType: mergedContext.pageType,
              urlPath: mergedContext.url ?? null,
            });
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem(SESSION_ID_KEY);
            }
            clearAssignedFunnel();
            setLoading(false);
            return { funnelId: '', funnelName: '', cached: false };
          }
          if (attempt === MAX_ASSIGN_RETRIES) {
            funnelLogger.error('Funnel assign failed after retries', err instanceof Error ? err : undefined, {
              assignError: String(err),
              pageType: mergedContext.pageType,
              urlPath: mergedContext.url ?? null,
              reason: 'assign_request_failed',
            });
            setAssignError(String(err));
            setLoading(false);
            throw err;
          }
          await new Promise<void>((r) => setTimeout(r, 1000 * (attempt + 1)));
        }
      }

      setLoading(false);
      return { funnelId: '', funnelName: '', cached: false };
    },
    [clearAssignedFunnel, client, countryCode, sessionId, setAssignedState],
  );

  const evaluatePageStep = useCallback(
    async (request: Omit<FunnelEvaluateStepRequest, 'sessionId'> & { funnelId?: string }): Promise<FunnelEvaluateStepResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { hasExperience: false, step: null };
      }
      const activeFunnelId = request.funnelId ?? assignedFunnelId;
      if (!activeFunnelId) {
        return { hasExperience: false, step: null };
      }
      const baseCtx = buildBaseContext();
      const resolvedPageContext = withResolvedURL({ ...baseCtx, ...request.pageContext });
      return client.evaluateStep({
        sessionId,
        funnelId: activeFunnelId,
        currentStepId: request.currentStepId,
        event: request.event,
        pageContext: resolvedPageContext,
      });
    },
    [sessionId, assignedFunnelId, client, ensureAssignedFunnelNotExpired],
  );

  const acceptOffer = useCallback(
    async (params: AcceptOfferParams): Promise<StepNavigationResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { complete: true, nextStep: null };
      }
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null };
      }
      if (params.action === 'add_to_order') {
        if (!params.orderId) {
          throw new Error('[FunnelProvider] add_to_order requires orderId — this is a caller bug');
        }
        return client.acceptUpsell({
          funnelId: assignedFunnelId,
          stepId: params.stepId,
          orderId: params.orderId,
        });
      }
      return client.acceptStep({
        funnelId: assignedFunnelId,
        stepId: params.stepId,
        sessionId,
        action: params.action ?? 'add_to_cart',
        productId: params.productId,
        variantId: params.variantId,
        quantity: params.quantity,
      });
    },
    [sessionId, assignedFunnelId, client, ensureAssignedFunnelNotExpired],
  );

  const declineOffer = useCallback(
    async (stepId: string, orderId?: string): Promise<StepNavigationResponse> => {
      if (ensureAssignedFunnelNotExpired()) {
        return { complete: true, nextStep: null };
      }
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null };
      }
      if (orderId) {
        return client.declineUpsell({
          funnelId: assignedFunnelId,
          stepId,
        });
      }
      return client.declineStep({
        funnelId: assignedFunnelId,
        stepId,
        sessionId,
      });
    },
    [sessionId, assignedFunnelId, client, ensureAssignedFunnelNotExpired],
  );

  const fetchTemplate = useCallback(
    (templateId: string) => client.fetchTemplate(templateId),
    [client],
  );

  const contextValue: FunnelContextValue = useMemo(
    () => ({
      config: stableConfig,
      sessionId,
      assignedFunnelId,
      assignedFunnelName,
      isAssignedFunnelLocked,
      loading,
      assignError,
      assignPageFunnel,
      lockAssignedFunnel,
      clearAssignedFunnel,
      evaluatePageStep,
      acceptOffer,
      declineOffer,
      fetchTemplate,
      activeExperience,
      setActiveExperience,
    }),
    [
      stableConfig,
      sessionId,
      assignedFunnelId,
      assignedFunnelName,
      isAssignedFunnelLocked,
      loading,
      assignError,
      assignPageFunnel,
      lockAssignedFunnel,
      clearAssignedFunnel,
      evaluatePageStep,
      acceptOffer,
      declineOffer,
      fetchTemplate,
      activeExperience,
    ],
  );

  const analyticsValue = useMemo(() => mergeAnalytics(analyticsPartial), [analyticsPartial]);

  return (
    <FunnelAnalyticsProvider value={analyticsValue}>
      <ProductImageProvider resolveProductImage={resolveProductImage}>
        <FunnelContext.Provider value={contextValue}>{children}</FunnelContext.Provider>
      </ProductImageProvider>
    </FunnelAnalyticsProvider>
  );
}

export function useFunnelContext(): FunnelContextValue {
  const context = useContext(FunnelContext);
  if (context === undefined) {
    throw new Error('useFunnelContext must be used within a FunnelProvider');
  }
  return context;
}

export function useOptionalFunnelContext(): FunnelContextValue | null {
  return useContext(FunnelContext) ?? null;
}
